import React, { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import Loader from 'components/Common/Loader/Loader';
import Trans from 'components/Common/Translation/Trans';
import ConversionScript from 'components/Veesual/ConversionScript';
import useLastOrder from './last-order.hook';
import useLargeSizeCookie from 'hooks/useLargeSizeCookie';
import { useCustomer } from 'hooks/useCustomer';
import useCurrentCountry from 'hooks/useCurrentCountry';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import { cartContext } from 'contexts/cartContext';
import { getRoutesUri, getUri } from 'services/generic';
import { IS_ENABLE as IS_VEESUAL_ENABLE } from 'services/mix-and-match';
import { GTM_PAGE_CONFIRMATION } from 'settings/gtm/gtmPageView';
import { Cart } from 'types/Cart';

import pictoSac from 'assets/img/picto-sac.png';
import styles from './ConfirmationPage.module.scss';

export default function ConfirmationPage(): JSX.Element {
  const { isFr } = useCurrentCountry();
  const { t } = useTranslation();
  const { resetPrivilegeCode } = useContext(cartContext);
  const { customer } = useCustomer();
  const { isLoaded, order, error } = useLastOrder();
  const { deleteCookie: deleteLSCookie } = useLargeSizeCookie();
  const localOrder: Cart = useMemo(
    () => JSON.parse(localStorage.getItem('order') || '{}'),
    []
  );

  useGtmPageView(
    GTM_PAGE_CONFIRMATION,
    '',
    {
      orderId: order?.order_id,
      cart: localOrder,
      isLoaded: isLoaded,
    },
    () => {
      localStorage.removeItem('add_to_cart');
    }
  );

  const transAdditionalComponents = {
    bTag: <b />,
    needHelpLink: <a href={getUri(isFr ? '/aide' : '/help')} />,
    contactLink: <a href={getUri('/contact')} />,
  };

  useEffect(() => {
    resetPrivilegeCode();
    deleteLSCookie();
  }, []);

  if (isLoaded && 404 === error?.status) {
    // Data loaded but no latest orders found.
    window.location.href = getRoutesUri<string>('cart');
  }

  if (!order || customer === undefined) {
    return (
      <>
        <RenderMetadata />
        <Loader />
      </>
    );
  }

  return (
    <div className={styles.confirmationPageContainer}>
      <RenderMetadata />
      {IS_VEESUAL_ENABLE && <ConversionScript products={localOrder.products} />}
      <div className={styles.thanksMessage}>
        <img className={styles.pictoSac} src={pictoSac} alt="picto-sac" />
        <span className={styles.title}>
          {t('LABEL_AFIBEL_THANKS')} {customer?.civility}{' '}
          <span className={styles.customerName}>{customer?.lastName} !</span>
        </span>
        {isFr && (
          <div className={styles.message}>{t('LABEL_ORDER_REGISTERED')}</div>
        )}
      </div>

      <div className={styles.confirmationContent}>
        <h3>{t('LABEL_ORDER_RECEIVED_TITLE')}</h3>
        <p>{t('LABEL_ORDER_RECEIVED_CONTENT')}</p>

        <h3>{t('LABEL_NEED_ADVICE_TITLE')}</h3>
        <p>
          <Trans
            i18nKey={'LABEL_NEED_ADVICE_CONTENT'}
            additionalComponents={transAdditionalComponents}
          />
        </p>

        <p>{t('LABEL_CUSTOMER_SERVICE_AVAILABILITY')}</p>
      </div>

      <a href={getUri('/')} className={styles.goHomeBtn}>
        {t('LABEL_BACK_STORE')}
      </a>
    </div>
  );
}

function RenderMetadata(): JSX.Element {
  const { t } = useTranslation();

  return (
    <Helmet>
      <title>
        {t('META_TITLE_TEMPLATE', {
          title: t('CONFIRMATION_PAYMENT_META_TITLE'),
        })}
      </title>
    </Helmet>
  );
}
